import { observer, useLocalObservable } from 'mobx-react';
import { useStore } from '../../../../../store/store';
import useHistory from '../../../../../hooks/useHistory';
import { API_ENDPOINTS } from '../../../../../api/endpoints';
import ProfilerNameSearch from '../shared/ProfilerNameSearch';
import CompanyNameResultItem from './CompanyNameResultItem';
import { updateUrlSearchQuery } from '../shared/profilerNameSearchUtils';

/**
 * CompanyNameSearch - Company name search using shared ProfilerNameSearch
 *
 * Wraps ProfilerNameSearch with company-specific configuration:
 * - Uses company API endpoint
 * - Renders CompanyNameResultItem for each result
 * - TD-1709: Uses URL-based navigation to standalone company details page
 *   (no data preload - the page loads extended data from API)
 *
 * @param {string} inputId - Unique ID for the input element (default: 'ps-input-searchKey')
 * @param {boolean} disabled - Whether search is disabled
 * @param {string} initialSearchQuery - Initial search query from URL parameter (for restoration)
 */
const CompanyNameSearch = observer(
  ({ inputId = 'ps-input-searchKey', disabled = false, initialSearchQuery = '' }) => {
    const { profilerAccessStore, utilsStore, authStore } = useStore();
    const { navigate } = useHistory();

    const state = useLocalObservable(() => ({
      // TD-1709: Track current search query for back navigation context
      currentSearchQuery: initialSearchQuery || '',

      setCurrentSearchQuery: (query) => {
        state.currentSearchQuery = query;
        updateUrlSearchQuery(query);
      },

      /**
       * Navigate to company details page
       * Uses URL-based navigation with company ID in path and ref param for back button label.
       * The company details page fetches data from API using the ID.
       *
       * @param {Object} company - Company data from search result
       * @param {Function} onFinally - Callback when navigation is complete
       * @param {Function} onSuccess - Custom success handler (e.g., for new tab)
       */
      navigateToCompany: (company, onFinally, onSuccess) => {
        if (onSuccess) {
          // Custom success handler (e.g., open in new tab)
          // Pass company ID, ref, and searchKey for new tab opening
          onSuccess({
            companyId: company.id,
            ref: 'search results',
            searchKey: state.currentSearchQuery || undefined
          });
        } else {
          // Navigate to standalone company details page using URL-based approach
          const path = utilsStore.getCompanyDetailsPath(company.id, {
            ref: 'search results',
            searchKey: state.currentSearchQuery || undefined
          });
          navigate(path);
        }
        onFinally?.();
      }
    }));

    const isSearchDisabled =
      (profilerAccessStore.isProfilerDemo && profilerAccessStore.demoSearchExceededLimit) ||
      disabled;

    return (
      <ProfilerNameSearch
        apiEndpoint={API_ENDPOINTS.GET_PROFILER_COMPANIES_BY_NAME}
        ResultItemComponent={CompanyNameResultItem}
        resultItemProps={{
          onNavigateToCompany: state.navigateToCompany,
          utilsStore,
          inputId
        }}
        placeholder="Type company name here"
        label="Company name and legal form"
        inputId={inputId}
        disabled={isSearchDisabled}
        initialSearchKey={initialSearchQuery}
        onSearchKeyChange={state.setCurrentSearchQuery}
      />
    );
  }
);

export default CompanyNameSearch;
